import { Handle, Node, NodeProps } from "@xyflow/react";
import { type ReactNode } from "react";
import cn from "classnames";
import { twMerge } from "tailwind-merge";
import Conditional from "~/components/Playground/Node/BaseNode/Conditional";
import {
  State,
  Outcome,
  BaseStep,
  isWaitingOnConcurrencyGroup,
  isWaitingOnPlatformLimit,
  NodeType,
} from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { bgColorForStep, textColorForStep } from "~/components/Playground/Node/colors";
import { useBuild, useMostRecentJob } from "~/components/build/Show/lib/BuildContext";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import {
  AnnotationsBadge,
  totalAnnotationCount,
} from "~/components/build/Show/components/Sidebar/TreeNode/AnnotationsBadge";
import { stepQueryOptions } from "~/components/build/Show/lib/queries/step";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { usePrefetchHandlers } from "~/components/build/Show/lib/usePrefetchHandlers";

type BaseStepProps = Node<{
  statistics?: BaseStep["statistics"];
  state?: State | null;
  outcome?: Outcome | null;
  if?: string;
  emphasis?: Emphasis;
  type?: string;
}>;

export enum Emphasis {
  Default,
  High,
  Low,
}

export default function BaseNode(
  props: {
    label?: ReactNode;
    icon?: ReactNode;
    children?: ReactNode;
    className?: string;
    hideStateIcon?: boolean;
    count?: ReactNode;
    actions?: ReactNode;
    disabled?: boolean;
    annotationCountsByStyle?: Record<string, number>;
  } & NodeProps<BaseStepProps>,
) {
  const { build } = useBuild({ strict: false });
  const { missingDependencies } = useStepDependencies(props.id);
  const track = useTrack();

  const shouldPrefetch =
    Features.ProgressiveBuildShow &&
    !!build &&
    ![NodeType.Root, NodeType.Group, NodeType.Input].includes(props.type as NodeType);

  const { onMouseEnter, onMouseLeave } = usePrefetchHandlers({
    queries: build
      ? [stepQueryOptions(build, props.id), stepJobsQueryOptions(build, props.id)]
      : [],
    enabled: shouldPrefetch,
  });

  const job = useMostRecentJob(props.id);
  const waitingOnConcurrencyGroup = isWaitingOnConcurrencyGroup(job?.state, props.data?.statistics);
  const waitingOnPlatformLimit = isWaitingOnPlatformLimit(job?.state, props.data?.statistics);
  const { state, outcome, type, emphasis = Emphasis.Default } = props.data;

  return (
    <>
      <Conditional if={props.data.if} />
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => track("Build Canvas Step Clicked", { type })}
        data-state={state}
        data-outcome={outcome}
        aria-disabled={props.disabled}
        className={twMerge(
          "group/node aria-disabled:cursor-not-allowed",
          "relative",
          "bg-white",
          "cursor-pointer",
          "rounded-xl",
          "p-3",
          "border-2",
          ["flex", "flex-col", "gap-3"],
          "transition-[color,opacity] duration-150",
          "max-w-[45ch]",
          cn({
            [textColorForStep({
              state,
              outcome,
              missingDependencies,
              waitingOnConcurrencyGroup,
              waitingOnPlatformLimit,
            })]: state,
            [bgColorForStep({ state, outcome }, missingDependencies, build)]: state,
            "shadow-depth-200": !state,
            "opacity-40 hover:opacity-100":
              state === State.WaitingForDependencies || state === State.Ignored,
            border: state === State.Finished && outcome === Outcome.SoftFailed,
            "ring-4 ring-[#086DD7] ring-offset-1": props.selected,
          }),
          props.className,
          cn({
            "opacity-40 hover:opacity-100": emphasis === Emphasis.Low,
            "opacity-100": emphasis === Emphasis.High,
          }),
        )}
      >
        <div className="flex items-center gap-2">
          {props.label && (
            <header
              role="heading"
              className={twMerge(
                "min-w-0 max-w-full flex-auto truncate text-base font-medium text-slate-900",
                cn({
                  "line-through": state === State.Ignored,
                }),
              )}
            >
              {props.label}
            </header>
          )}

          <span className="ml-auto flex items-center gap-1">
            {props.count}
            {props.icon ? (
              props.icon
            ) : (
              <StateIcon
                {...props.data}
                missingDependencies={missingDependencies}
                waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
                waitingOnPlatformLimit={waitingOnPlatformLimit}
              />
            )}
          </span>

          {props.actions && props.actions}
        </div>
        {props.annotationCountsByStyle &&
          totalAnnotationCount(props.annotationCountsByStyle) > 0 && (
            <AnnotationsBadge
              countsByStyle={props.annotationCountsByStyle}
              className={twMerge(
                "absolute -right-2 -top-2 cursor-pointer -space-x-3 group-hover/node:-space-x-0.5 [&>span]:transition-[margin] [&>span]:delay-150 [&>span]:duration-150",
                props.selected && "-space-x-0.5",
                props.selected && "[&>span]:transition-none",
              )}
            />
          )}
        {props.children}
      </div>

      {props.targetPosition && (
        <Handle
          type="target"
          position={props.targetPosition}
          isConnectable={props.isConnectable}
          className={cn({ "opacity-0": !props.isConnectable })}
        />
      )}
      {props.sourcePosition && (
        <Handle
          type="source"
          position={props.sourcePosition}
          isConnectable={props.isConnectable}
          className={cn({ "opacity-0": !props.isConnectable })}
        />
      )}
    </>
  );
}
